import React, { useState, useEffect } from "react";
import { Tabs, Spin, Card, Row, Col, Button } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { fetchEnrollmentPolicyType, getsummaryhospitaldata, getenrollmentsummarydata, getSummaryPolicyData, getSummaryRelationBandWiseData } from "../../Action/Actions";
import { getUserData } from "../login/LoginSlice";
import { ExportOutlined, TeamOutlined, UserOutlined, FileAddOutlined, FireOutlined } from "@ant-design/icons";
import "./summary.css";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import VibeLogo from '../../Images/logo.png';
import { selectFinancialYears, selectSelectedYearId } from "../../Redux/financialYearSlice";
import { addNotoSansFont } from '../../assest/fonts/noto-sans-regular-font';

const Summary = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { module_option_ids } = location.state || {};
  const AuthData = useSelector(getUserData);
  const [activeTab, setActiveTab] = useState("GMC");
  const [selectedPolicyNumber, setSelectedPolicyNumber] = useState(null);

  // --- Redux Integration for Financial Year ---
  const selectedYearId = useSelector(selectSelectedYearId);
  const financialYears = useSelector(selectFinancialYears);
  const selectedYearName = financialYears.find(year => year.id === selectedYearId)?.year_name || "N/A";
  // --- End of Redux Integration ---

  const [escalationData, setEscalationData] = useState({});
  const [summaryData, setSummaryData] = useState({});
  const [loading, setLoading] = useState(false);
  const policyTypes = Object.keys(escalationData || {});
  const user = useSelector(getUserData);
  const [enrollmentSummaryData, setenrollmentSummaryData] = useState({});
  const [summaryPolicyData, setSummaryPolicyData] = useState({});
  const [summaryRelationBandWiseData, setSummaryRelationBandWiseData] = useState({});

  // --- Updated useEffect to align with the new action signature ---
  useEffect(() => {
    // We check for both corporate_id and selectedYearId to ensure we have all needed data.
    if (AuthData?.corporate_id && selectedYearId) {
      setLoading(true);
      // Reset dependent states when year changes to avoid showing stale data
      setEscalationData({});
      setSelectedPolicyNumber(null);
      setSummaryData({});
      setenrollmentSummaryData({});
      setSummaryPolicyData({});
      setSummaryRelationBandWiseData({});

      // The action now only needs the corporate_id.
      // It gets the financialYearId from the Redux state internally.
      dispatch(fetchEnrollmentPolicyType(AuthData.corporate_id))
        .then((data) => {
          const policy_type_list = data.policy_type_list || {};
          setEscalationData(policy_type_list);
          
          // Get policy types that actually have data (not empty arrays)
          const availableTypes = Object.keys(policy_type_list).filter(
            (key) => policy_type_list[key] && policy_type_list[key].length > 0
          );
          
          // Use the first available type that has data
          const newActiveTab = availableTypes.length > 0 ? availableTypes[0] : "";
          setActiveTab(newActiveTab);

          const firstPolicyNumber = policy_type_list[newActiveTab]?.[0];
          setSelectedPolicyNumber(firstPolicyNumber || null);
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
          console.error("Error fetching escalation matrix:", err);
        });
    }
    // The dependency array still includes selectedYearId to trigger this effect on year change.
  }, [dispatch, AuthData, selectedYearId]); 

  useEffect(() => {
    if (selectedPolicyNumber?.policy_id) {
      setLoading(true);
      dispatch(getsummaryhospitaldata(selectedPolicyNumber.policy_id))
        .then((data) => {
          setSummaryData(data);
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
          console.error("Error fetching summary data:", err);
        });
    } else {
        setSummaryData({}); // Clear data if no policy is selected
    }
  }, [dispatch, selectedPolicyNumber]);

  useEffect(() => {
    if (selectedPolicyNumber?.policy_id) {
      setLoading(true);
      dispatch(getenrollmentsummarydata(selectedPolicyNumber.policy_id))
        .then((data) => {
          setenrollmentSummaryData(data);
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
          console.error("Error fetching enrollment summary data:", err);
        });
    } else {
        setenrollmentSummaryData({});
    }
  }, [dispatch, selectedPolicyNumber]);

  useEffect(() => {
    if (selectedPolicyNumber?.policy_id) {
      setLoading(true);
      dispatch(getSummaryPolicyData(selectedPolicyNumber.policy_id))
        .then((data) => {
          setSummaryPolicyData(data);
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
          console.error("Error fetching policy summary data:", err);
        });
    } else {
        setSummaryPolicyData({});
    }
  }, [dispatch, selectedPolicyNumber]);

  useEffect(() => {
    if (selectedPolicyNumber?.policy_id) {
      setLoading(true);
      dispatch(getSummaryRelationBandWiseData(selectedPolicyNumber.policy_id))
        .then((data) => {
          setSummaryRelationBandWiseData(data);
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
          console.error("Error fetching relation/band wise summary data:", err);
        });
    } else {
        setSummaryRelationBandWiseData({});
    }
  }, [dispatch, selectedPolicyNumber]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    const policyNumber = escalationData[key]?.[0];
    setSelectedPolicyNumber(policyNumber || null);
  };

  const handlePolicyNumberClick = (key) => {
    // Find the policy object based on the key (either policy_number or policy-${index})
    const policy = escalationData[activeTab]?.find((p, index) => {
      const policyKey = p.policy_number || `policy-${index}`;
      return policyKey === key;
    });
    
    if (policy) {
      setSelectedPolicyNumber(policy);
    }
  };

  // Helper functions for PDF formatting
  const formatCurrency = (value) => {
  if (value === null || value === undefined) return '₹0.00';

  const num =
    typeof value === 'number'
      ? value
      : parseFloat(String(value).replace(/[,₹Rs\s]/g, ''));

  if (isNaN(num)) return '₹0.00';

  return `₹${new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num)}`;
};
  const formatPercentage = (value) => {
      const num = parseFloat(String(value).replace(/%/g, ''));
      if (value === null || value === undefined || isNaN(num)) {
          return '0.00%';
      }
      return `${num.toFixed(2)}%`;
  };

  const exportToPDF = () => {
    if (!summaryPolicyData || !enrollmentSummaryData || !summaryRelationBandWiseData || !summaryData) {
        console.error("Data not loaded yet for PDF export.");
        return;
    }

    const doc = new jsPDF();

    // Initialize custom font
    addNotoSansFont(doc);

    doc.setFont('NotoSans', 'normal');

    let startY = 10;
    const pageMargin = 14;

    const drawTitle = (text, y) => {
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text(text, pageMargin, y, { align: 'left' });
        doc.setFont(undefined, 'normal');
        return y + 5;
    };

    const commonStyles = {
        theme: 'grid',
        styles: { font: 'NotoSans', fontSize: 8, cellPadding: 1.5, overflow: 'linebreak' },
        headStyles: { fontStyle: 'bold', fillColor: '#ffffff', textColor: '#000000', lineWidth: 0.1, lineColor: [100, 100, 100], halign: 'center', valign: 'middle' },
        bodyStyles: { lineWidth: 0.1, lineColor: [100, 100, 100] },
        didDrawPage: (data) => {
            startY = data.cursor.y + 10; // Reset startY for next table on new page
        }
    };
    
    // --- Page 1 ---
    doc.addImage(VibeLogo, 'PNG', pageMargin, 15, 40, 10);
    doc.setFontSize(16);
    doc.text("Policy Summary", doc.internal.pageSize.getWidth() / 2, 40, { align: "center" });

    const corporateName = summaryPolicyData.policy_type_list?.corporate_name || "N/A";
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text(`M/S.${corporateName}`, doc.internal.pageSize.getWidth() / 2, 50, { align: "center" });
    startY = 55;
    
    const policyPeriod = `${summaryPolicyData.policy_type_list?.policy_start_date || "N/A"} to ${summaryPolicyData.policy_type_list?.policy_end_date || "N/A"}`;
    const policyDetailsBody = [
        [{ content: `M/S.${corporateName}`, colSpan: 6, styles: { fontStyle: 'bold', halign: 'center' } }],
        [{ content: 'INSURANCE COMPANY NAME', styles: { fontStyle: 'bold' } }, { content: summaryPolicyData.policy_type_list?.select_insurer || "N/A", colSpan: 5 }],
        [{ content: 'TPA NAME', styles: { fontStyle: 'bold' } }, { content: summaryPolicyData.policy_type_list?.select_tpa || "N/A", colSpan: 5 }],
        [{ content: 'POLICY NO', styles: { fontStyle: 'bold' } }, summaryPolicyData.policy_type_list?.policy_number || "N/A", { content: 'POLICY PERIOD', styles: { fontStyle: 'bold' } }, { content: policyPeriod, colSpan: 3 }],
        [{ content: 'PREMIUM SUMMARY', rowSpan: 2, styles: { valign: 'middle', fontStyle: 'bold' } }, 'Inception Premium', 'Additional Premium', 'Deletion Premium', 'NET Premium', 'Active Lives'],
        [
            formatCurrency(summaryPolicyData.premium_summary?.inception_premium),
            formatCurrency(summaryPolicyData.premium_summary?.additional_premium),
            formatCurrency(summaryPolicyData.premium_summary?.deletion_premium),
            formatCurrency(summaryPolicyData.premium_summary?.net_premium),
            summaryPolicyData.active_summary ?? '0',
        ],
    ];
    autoTable(doc, { ...commonStyles, body: policyDetailsBody, startY, columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' } } });
    startY = doc.lastAutoTable.finalY + 5;

    startY = drawTitle('ENROLLMENT SUMMARY', startY);
    const enrollmentHead = [['Lives', 'Inception Lives', 'Additional Lives', 'Deletion Lives', 'Active Lives']];
    const enrollmentBodyRaw = (enrollmentSummaryData.enrollment_summary || []).filter(item => item.relationship?.toLowerCase() !== 'total');
    const enrollmentTotals = enrollmentBodyRaw.reduce((acc, item) => ({
        inception: acc.inception + (parseInt(item.inception_lives, 10) || 0),
        additional: acc.additional + (parseInt(item.additional_lives, 10) || 0),
        deletion: acc.deletion + (parseInt(item.deletion_lives, 10) || 0),
        active: acc.active + (parseInt(item.active_lives, 10) || 0),
    }), { inception: 0, additional: 0, deletion: 0, active: 0 });
    const enrollmentBody = enrollmentBodyRaw.map(item => [item.relationship, item.inception_lives, item.additional_lives, item.deletion_lives, item.active_lives]);
    enrollmentBody.push([{ content: 'Total', styles: { font: 'NotoSans', fontStyle: 'normal' } }, { content: enrollmentTotals.inception, styles: { font: 'NotoSans', fontStyle: 'normal' } }, { content: enrollmentTotals.additional, styles: { font: 'NotoSans', fontStyle: 'normal' } }, { content: enrollmentTotals.deletion, styles: { font: 'NotoSans', fontStyle: 'normal' } }, { content: enrollmentTotals.active, styles: { font: 'NotoSans', fontStyle: 'normal' } }]);
    autoTable(doc, { ...commonStyles, head: enrollmentHead, body: enrollmentBody, startY, columnStyles: { 0: { halign: 'left' }, 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' } } });
    startY = doc.lastAutoTable.finalY + 5;

    startY = drawTitle('CLAIM RATIO (%) SUMMARY', startY);
    const claimRatioHead = [['Net premium', 'Earned premium', 'Incurred Amount', 'Claim Ratio (%)', 'Earned Ratio (%)']];
    const claimRatioBody = [[
        formatCurrency(summaryPolicyData.claim_ratio_summary?.Net_Premium),
        formatCurrency(summaryPolicyData.claim_ratio_summary?.Earned_Premium),
        formatCurrency(summaryPolicyData.claim_ratio_summary?.Incurred_Amount),
        formatPercentage(summaryPolicyData.claim_ratio_summary?.Claim_Ratio),
        formatPercentage(summaryPolicyData.claim_ratio_summary?.Earned_Ratio),
    ]];
    autoTable(doc, { ...commonStyles, head: claimRatioHead, body: claimRatioBody, startY, columnStyles: { 0: { halign: 'right' }, 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' } } });

    // --- Page 2 ---
    doc.addPage();
    startY = 15;

    startY = drawTitle('CLAIM STATUS SUMMARY', startY);
    const claimStatusHead = [
        [{ content: 'STATUS', rowSpan: 2, styles: { valign: 'middle' } }, { content: 'CASHLESS', colSpan: 3, styles: { halign: 'center' } }, { content: 'REIMBURSEMENT', colSpan: 3, styles: { halign: 'center' } }, { content: 'TOTAL', colSpan: 3, styles: { halign: 'center' } }],
        ['Total Claim', 'Claimed Amt', 'Incurred Amt', 'Total Claim', 'Claimed Amt', 'Incurred Amt', 'Total Claim', 'Claimed Amt', 'Incurred Amt']
    ];
    const statusOrder = ['Paid', 'Closed', 'Rejected', 'Under Process'];
    const claimStatusData = enrollmentSummaryData.claim_status_summary || {};
    const claimStatusBody = statusOrder.map(status => {
        const data = claimStatusData[status] || {};
        const cashlessCount = data.Cashless?.count || 0;
        const cashlessClaimed = data.Cashless?.claimed || 0;
        const cashlessIncurred = data.Cashless?.incurred || 0;
        const reimbursementCount = data.Reimbursement?.count || 0;
        const reimbursementClaimed = data.Reimbursement?.claimed || 0;
        const reimbursementIncurred = data.Reimbursement?.incurred || 0;
        return [
            status, cashlessCount, formatCurrency(cashlessClaimed), formatCurrency(cashlessIncurred),
            reimbursementCount, formatCurrency(reimbursementClaimed), formatCurrency(reimbursementIncurred),
            cashlessCount + reimbursementCount, formatCurrency(cashlessClaimed + reimbursementClaimed), formatCurrency(cashlessIncurred + reimbursementIncurred)
        ];
    });
    const claimStatusTotals = claimStatusBody.reduce((acc, row) => {
        acc[0] += row[1]; acc[1] += parseFloat(String(row[2]).replace(/[,₹Rs\s]/g, '')); acc[2] += parseFloat(String(row[3]).replace(/[,₹Rs\s]/g, ''));
        acc[3] += row[4]; acc[4] += parseFloat(String(row[5]).replace(/[,₹Rs\s]/g, '')); acc[5] += parseFloat(String(row[6]).replace(/[,₹Rs\s]/g, ''));
        acc[6] += row[7]; acc[7] += parseFloat(String(row[8]).replace(/[,₹Rs\s]/g, '')); acc[8] += parseFloat(String(row[9]).replace(/[,₹Rs\s]/g, ''));
        return acc;
    }, new Array(10).fill(0));
    claimStatusBody.push([
        { content: 'Total', styles: { font: 'NotoSans', fontStyle: 'normal' } }, { content: claimStatusTotals[0], styles: { font: 'NotoSans', fontStyle: 'normal' } }, { content: formatCurrency(claimStatusTotals[1]), styles: { font: 'NotoSans', fontStyle: 'normal' } }, { content: formatCurrency(claimStatusTotals[2]), styles: { font: 'NotoSans', fontStyle: 'normal' } },
        { content: claimStatusTotals[3], styles: { font: 'NotoSans', fontStyle: 'normal' } }, { content: formatCurrency(claimStatusTotals[4]), styles: { font: 'NotoSans', fontStyle: 'normal' } }, { content: formatCurrency(claimStatusTotals[5]), styles: { font: 'NotoSans', fontStyle: 'normal' } },
        { content: claimStatusTotals[6], styles: { font: 'NotoSans', fontStyle: 'normal' } }, { content: formatCurrency(claimStatusTotals[7]), styles: { font: 'NotoSans', fontStyle: 'normal' } }, { content: formatCurrency(claimStatusTotals[8]), styles: { font: 'NotoSans', fontStyle: 'normal' } }
    ]);
    autoTable(doc, { ...commonStyles, head: claimStatusHead, body: claimStatusBody, startY, columnStyles: { 0: { halign: 'left' }, 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' }, 8: { halign: 'right' }, 9: { halign: 'right' } } });
    startY = doc.lastAutoTable.finalY + 5;

    startY = drawTitle('AGE-WISE SETTLED CLAIM SUMMARY', startY);
    const ageWiseHead = [['Age-Wise', 'Total Claims', 'Settled Amt', 'Percentage of Claims', 'ACS']];
    const ageWiseBodyRaw = (enrollmentSummaryData.agewise_claim_summary || []).filter(item => item["Age-Wise"]?.toLowerCase() !== 'total');
    const ageWiseBody = ageWiseBodyRaw.map(item => [item["Age-Wise"], item["Total Claims"], formatCurrency(item["Settled Amt"]), formatPercentage(item["Percentage of Claims"]), formatCurrency(item["ACS"])]);
    const ageWiseTotals = ageWiseBodyRaw.reduce((acc, item) => ({
        claims: acc.claims + (parseInt(item["Total Claims"], 10) || 0),
        settled: acc.settled + (parseFloat(String(item["Settled Amt"]).replace(/[,₹Rs\s]/g, '')) || 0),
    }), { claims: 0, settled: 0 });
    ageWiseBody.push([{ content: 'Total', styles: { font: 'NotoSans', fontStyle: 'normal' } }, { content: ageWiseTotals.claims, styles: { font: 'NotoSans', fontStyle: 'normal' } }, { content: formatCurrency(ageWiseTotals.settled), styles: { font: 'NotoSans', fontStyle: 'normal' } }, { content: '100.00%', styles: { font: 'NotoSans', fontStyle: 'normal' } }, '']);
    autoTable(doc, { ...commonStyles, head: ageWiseHead, body: ageWiseBody, startY, columnStyles: { 0: { halign: 'left' }, 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' } } });
    startY = doc.lastAutoTable.finalY + 5;

    startY = drawTitle('SETTLED CLAIM TAT SUMMARY', startY);
    const tatHead = [['Settled Claim Status', 'Internal Processing Tat (Number)', 'Internal Processing Tat (%)']];
    const tatBodyRaw = (enrollmentSummaryData.settled_claim_tat_summary || []).filter(item => item["Settled Claim Status"]?.toLowerCase() !== 'total');
    const tatBody = tatBodyRaw.map(item => [item["Settled Claim Status"], item["Internal Processing Tat (Number)"], formatPercentage(item["Internal Processing Tat (%)"])]);
    const tatTotal = tatBodyRaw.reduce((acc, item) => acc + (parseInt(item["Internal Processing Tat (Number)"], 10) || 0), 0);
    tatBody.push([{ content: 'Total', styles: { font: 'NotoSans', fontStyle: 'normal' } }, { content: tatTotal, styles: { font: 'NotoSans', fontStyle: 'normal' } }, { content: '100.00%', styles: { font: 'NotoSans', fontStyle: 'normal' } }]);
    autoTable(doc, { ...commonStyles, head: tatHead, body: tatBody, startY, columnStyles: { 0: { halign: 'left' }, 1: { halign: 'right' }, 2: { halign: 'right' } } });
    startY = doc.lastAutoTable.finalY + 5;

    startY = drawTitle('SETTLED (ACS)', startY);
    const acsHead = [
        [{ content: 'STATUS', rowSpan: 2, styles: { valign: 'middle' } }, { content: 'CASHLESS', colSpan: 3, styles: { halign: 'center' } }, { content: 'REIMBURSEMENT', colSpan: 3, styles: { halign: 'center' } }],
        ['Total Claim', 'Settled Amt', 'ACS', 'Total Claim', 'Settled Amt', 'ACS']
    ];
    const settledAcsData = summaryPolicyData.settled_ACS_summary || {};
    const paidRow = ['Paid', settledAcsData.Cashless?.total_claims || 0, formatCurrency(settledAcsData.Cashless?.settled_amt), formatCurrency(settledAcsData.Cashless?.acs), settledAcsData.Reimbursement?.total_claims || 0, formatCurrency(settledAcsData.Reimbursement?.settled_amt), formatCurrency(settledAcsData.Reimbursement?.acs)];
    const totalRow = [{ content: 'Total', styles: { font: 'NotoSans', fontStyle: 'normal' } }, { content: settledAcsData.Cashless?.total_claims || 0, styles: { font: 'NotoSans', fontStyle: 'normal' } }, { content: formatCurrency(settledAcsData.Cashless?.settled_amt), styles: { font: 'NotoSans', fontStyle: 'normal' } }, { content: formatCurrency(settledAcsData.Cashless?.acs), styles: { font: 'NotoSans', fontStyle: 'normal' } }, { content: settledAcsData.Reimbursement?.total_claims || 0, styles: { font: 'NotoSans', fontStyle: 'normal' } }, { content: formatCurrency(settledAcsData.Reimbursement?.settled_amt), styles: { font: 'NotoSans', fontStyle: 'normal' } }, { content: formatCurrency(settledAcsData.Reimbursement?.acs), styles: { font: 'NotoSans', fontStyle: 'normal' } }];
    autoTable(doc, { ...commonStyles, head: acsHead, body: [paidRow, totalRow], startY, columnStyles: { 0: { halign: 'left' }, 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' } } });
    
    // --- Page 3 ---
    doc.addPage();
    startY = 15;

    startY = drawTitle('RELATION WISE SETTLED CLAIM SUMMARY', startY);
    const relationWiseHead = [['Relation Wise', 'Total Claims', 'Settled Amt', 'Percentage of Claims', 'ACS']];
    const relationWiseBodyRaw = (summaryRelationBandWiseData.relationwise_claim_summary || []).filter(item => item["Relation Wise"]?.toLowerCase() !== 'total');
    const relationWiseBody = relationWiseBodyRaw.map(item => [item["Relation Wise"], item["Total Claims"], formatCurrency(item["Settled Amt"]), formatPercentage(item["Percentage of Claims"]), formatCurrency(item["ACS"])]);
    const relationWiseTotals = relationWiseBodyRaw.reduce((acc, item) => ({
        claims: acc.claims + (parseInt(item["Total Claims"], 10) || 0),
        settled: acc.settled + (parseFloat(String(item["Settled Amt"]).replace(/[,₹Rs\s]/g, '')) || 0),
    }), { claims: 0, settled: 0 });
    relationWiseBody.push([{ content: 'Total', styles: { font: 'NotoSans', fontStyle: 'normal' } }, { content: relationWiseTotals.claims, styles: { font: 'NotoSans', fontStyle: 'normal' } }, { content: formatCurrency(relationWiseTotals.settled), styles: { font: 'NotoSans', fontStyle: 'normal' } }, { content: '100.00%', styles: { font: 'NotoSans', fontStyle: 'normal' } }, '']);
    autoTable(doc, { ...commonStyles, head: relationWiseHead, body: relationWiseBody, startY, columnStyles: { 0: { halign: 'left' }, 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' } } });
    startY = doc.lastAutoTable.finalY + 5;

    startY = drawTitle('AMOUNT BAND SETTLED CLAIM SUMMARY', startY);
    const amountBandHead = [['AMOUNT BAND', 'Total Claims', 'Settled Amt', '% of Claims', 'ACS']];
    const amountBandBodyRaw = (summaryRelationBandWiseData.amount_band_summary || []).filter(item => item["AMOUNT BAND"]?.toLowerCase() !== 'total');
    const amountBandBody = amountBandBodyRaw.map(item => [item["AMOUNT BAND"], item["Total Claims"], formatCurrency(item["Settled Amt"]), formatPercentage(item["% of Claims"]), formatCurrency(item["ACS"])]);
    const amountBandTotals = amountBandBodyRaw.reduce((acc, item) => ({
        claims: acc.claims + (parseInt(item["Total Claims"], 10) || 0),
        settled: acc.settled + (parseFloat(String(item["Settled Amt"]).replace(/[,₹Rs\s]/g, '')) || 0),
    }), { claims: 0, settled: 0 });
    amountBandBody.push([{ content: 'Total', styles: { font: 'NotoSans', fontStyle: 'normal' } }, { content: amountBandTotals.claims, styles: { font: 'NotoSans', fontStyle: 'normal' } }, { content: formatCurrency(amountBandTotals.settled), styles: { font: 'NotoSans', fontStyle: 'normal' } }, { content: '100.00%', styles: { font: 'NotoSans', fontStyle: 'normal' } }, '']);
    autoTable(doc, { ...commonStyles, head: amountBandHead, body: amountBandBody, startY, columnStyles: { 0: { halign: 'left' }, 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' } } });
    startY = doc.lastAutoTable.finalY + 5;

    startY = drawTitle('LEVEL OF CARE SETTLED CLAIM SUMMARY', startY);
    const levelCareHead = [
        [{ content: 'Level of Care', rowSpan: 2, styles: { valign: 'middle' } }, { content: 'MEDICAL', colSpan: 3, styles: { halign: 'center' } }, { content: 'SURGICAL', colSpan: 3, styles: { halign: 'center' } }],
        ['Total Claim', 'Settled Amt', 'ACS', 'Total Claim', 'Settled Amt', 'ACS']
    ];
    const levelCareBodyRaw = (summaryRelationBandWiseData.level_of_care_summary || []).filter(item => item["Level of Care"]?.toLowerCase() !== 'total');
    const levelCareBody = levelCareBodyRaw.map(item => [item["Level of Care"], item["Total Claim (Medical)"], formatCurrency(item["Settled Amt (Medical)"]), formatCurrency(item["ACS (Medical)"]), item["Total Claim (Surgical)"], formatCurrency(item["Settled Amt (Surgical)"]), formatCurrency(item["ACS (Surgical)"])]);
    const levelCareTotals = levelCareBodyRaw.reduce((acc, item) => {
        acc.med_claims += parseInt(item["Total Claim (Medical)"], 10) || 0;
        acc.med_settled += parseFloat(String(item["Settled Amt (Medical)"]).replace(/[,₹Rs\s]/g, '')) || 0;
        acc.surg_claims += parseInt(item["Total Claim (Surgical)"], 10) || 0;
        acc.surg_settled += parseFloat(String(item["Settled Amt (Surgical)"]).replace(/[,₹Rs\s]/g, '')) || 0;
        return acc;
    }, { med_claims: 0, med_settled: 0, surg_claims: 0, surg_settled: 0 });
    levelCareBody.push([{ content: 'Total', styles: { font: 'NotoSans', fontStyle: 'normal' } }, { content: levelCareTotals.med_claims, styles: { font: 'NotoSans', fontStyle: 'normal' } }, { content: formatCurrency(levelCareTotals.med_settled), styles: { font: 'NotoSans', fontStyle: 'normal' } }, '', { content: levelCareTotals.surg_claims, styles: { font: 'NotoSans', fontStyle: 'normal' } }, { content: formatCurrency(levelCareTotals.surg_settled), styles: { font: 'NotoSans', fontStyle: 'normal' } }, '']);
    autoTable(doc, { ...commonStyles, head: levelCareHead, body: levelCareBody, startY, columnStyles: { 0: { halign: 'left' }, 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' } } });

    // --- Page 4 onwards ---
    if (summaryData.top_diseases?.length) {
        doc.addPage();
        startY = 15;
        startY = drawTitle('DISEASE CATEGORY WISE SETTLED CLAIM SUMMARY (BASE ON AMOUNT)', startY);
        const diseaseHead = [
            [{ content: 'DISEASE CATEGORY', rowSpan: 2, styles: { valign: 'middle' } }, { content: 'CASHLESS', colSpan: 2, styles: { halign: 'center' } }, { content: 'REIMBURSEMENT', colSpan: 2, styles: { halign: 'center' } }, { content: 'TOTAL', colSpan: 2, styles: { halign: 'center' } }],
            ['NO', 'AMOUNT', 'NO', 'AMOUNT', 'NO', 'AMOUNT']
        ];
        const diseaseBody = summaryData.top_diseases.map(d => [d.disease_category, d.cashless_no, formatCurrency(d.cashless_amount), d.reimbursement_no, formatCurrency(d.reimbursement_amount), d.total_no, formatCurrency(d.total_amount)]);
        autoTable(doc, { ...commonStyles, head: diseaseHead, body: diseaseBody, startY, columnStyles: { 0: { cellWidth: 'auto', halign: 'left' }, 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' } } });
    }

    if (summaryData.hospital_claims?.length) {
        doc.addPage();
        startY = 15;
        startY = drawTitle('TOP HOSPITAL-WISE SETTLED CLAIM SUMMARY (BASE ON AMOUNT)', startY);
        const hospitalHead = [
            [{ content: 'HOSPITAL NAME WITH CITY', rowSpan: 2, styles: { valign: 'middle' } }, { content: 'NETWORK TYPE', rowSpan: 2, styles: { valign: 'middle' } }, { content: 'CASHLESS', colSpan: 2, styles: { halign: 'center' } }, { content: 'REIMBURSEMENT', colSpan: 2, styles: { halign: 'center' } }, { content: 'TOTAL', colSpan: 2, styles: { halign: 'center' } }],
            ['NO', 'AMOUNT', 'NO', 'AMOUNT', 'NO', 'AMOUNT']
        ];
        const hospitalBody = summaryData.hospital_claims.map(c => [c.hospital_name, c.network_type, c.cashless_no, formatCurrency(c.cashless_amount), c.reimbursement_no, formatCurrency(c.reimbursement_amount), c.total_no, formatCurrency(c.total_amount)]);
        autoTable(doc, { ...commonStyles, head: hospitalHead, body: hospitalBody, startY, columnStyles: { 0: { cellWidth: 'auto', halign: 'left' }, 1: { halign: 'center' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' } } });
    }

    doc.save(`Policy_Summary_${corporateName}.pdf`);
  };

  const EscalationLevels = ({ levels }) => (
    <Card>
      <Row
        gutter={16}
        className="mb-3 justify-content-between align-items-center px-3"
        style={{ display: "flex", justifySelf: "flex-end" }}
      >
        <Col xs={24} sm={2} md={2} lg={2} xl={2}>
          <Button
            type="primary"
            onClick={exportToPDF}
            icon={<ExportOutlined />}
            className="d-flex"
          >
            Export
          </Button>
        </Col>
      </Row>
         <div class="col-lg-12 col-md-12">
        <div class="row card-header">
           <table class="table table-bordered">
             <thead>
 
               <tr>
                 <td colspan="6" class="text-center">
                   <strong>{summaryPolicyData.policy_type_list?.corporate_name || "N/A"}</strong>
                 </td>
               </tr>
               <tr>
               <td>
                   <strong>INSURANCE COMPANY NAME</strong>
                 </td>
                 <td colspan="5">{summaryPolicyData.policy_type_list?.select_insurer || "N/A"}</td>
               </tr>
               <tr>
                 <td>
                   <strong>TPA NAME</strong>
                </td>
                <td colspan="5">{summaryPolicyData.policy_type_list?.select_tpa || "N/A"}</td>
               </tr>
               <tr>
                 <td>
                   <strong>POLICY NO</strong>
                 </td>
                 <td>{summaryPolicyData.policy_type_list?.policy_number || "N/A"}</td>
                 <td>
                   <strong>POLICY PERIOD</strong>
                 </td>
                 <td colspan="3">
                 {summaryPolicyData.policy_type_list?.policy_start_date || "N/A"} <strong>to</strong> {summaryPolicyData.policy_type_list?.policy_end_date || "N/A"}
                 </td>
               </tr>
               <tr>
                 <td rowspan="2">
                   <strong>PREMIUM SUMMARY</strong>
                 </td>
                 <td>Inception Premium</td>
                 <td>Additional Premium</td>
                 <td>Deletion Premium</td>
                 <td>NET Premium</td>
                 <td>Active Lives</td>
               </tr>
               <tr>
                 <td>
                   <strong>{summaryPolicyData.premium_summary?.inception_premium}</strong>
                 </td>
                 <td>
                   <strong>{summaryPolicyData.premium_summary?.additional_premium}</strong>
                 </td>
                 <td>
                   <strong>{summaryPolicyData.premium_summary?.deletion_premium}</strong>
                 </td>
                 <td>
                   <strong>{summaryPolicyData.premium_summary?.net_premium}</strong>
                 </td>
                 <td>
                   <strong>{summaryPolicyData.active_summary}</strong>
                 </td>
               </tr>
            
             </thead>
           </table>
         </div>
       </div>

       <div class="col-lg-12 col-md-12">
         <div class="row card-header">
          <table class="table table-bordered">
             <thead>
               <tr class="text-center">
                 <th colspan="5">ENROLLMENT SUMMARY</th>
               </tr>
               <tr>
                 <th>
                   <strong>Lives</strong>
                 </th>
                 <th>
                   <strong>Inception Lives</strong>
                 </th>
                 <th>
                   <strong>Additional Lives</strong>
                 </th>
                 <th>
                   <strong>Deletion Lives</strong>
                 </th>
                 <th>
                   <strong>Active Lives</strong>
                 </th>
               </tr>
               
                {enrollmentSummaryData.enrollment_summary?.map((item, index) => (
                  <tr key={index} className="total-row">
                    <td>{item.relationship}</td>
                    <td>{item.inception_lives}</td>
                    <td>{item.additional_lives}</td>
                    <td>{item.deletion_lives}</td>
                    <td>{item.active_lives}</td>
                  </tr>
                ))}
             
             </thead>
           </table>
         </div>
       </div>

       <div class="col-lg-12 col-md-12">
         <div class="row card-header">
           <table class="table table-bordered">
             <thead>
               <tr class="text-center">
                 <th colspan="5">CLAIM RATIO (%) SUMMARY</th>
               </tr>
               <tr>
                 <th>
                   <strong>Net premium</strong>
                 </th>
                 <th>
                  <strong>Earned premium</strong>
                 </th>
                 <th>
                  <strong>Incurred Amount</strong>
                 </th>
                 <th>
                   <strong>Claim Ratio (%)</strong>
                 </th>
                 <th>
                   <strong>Earned Ratio (%)</strong>
                 </th>
               </tr>
               <tr>
                 <td>{summaryPolicyData.claim_ratio_summary?.Net_Premium}</td>
                 <td>{summaryPolicyData.claim_ratio_summary?.Earned_Premium}</td>
                 <td>{summaryPolicyData.claim_ratio_summary?.Incurred_Amount}</td>
                 <td>{summaryPolicyData.claim_ratio_summary?.Claim_Ratio}</td>
                 <td>{summaryPolicyData.claim_ratio_summary?.Earned_Ratio}</td>
               </tr>
             </thead>
           </table>
         </div>
       </div>

       <div class="col-lg-12 col-md-12">
         <div class="row card-header">
           <table class="table table-bordered">
             <thead>
               <tr class="text-center">
                 <th colspan="10">CLAIM STATUS SUMMARY</th>
               </tr>
               <tr>
                 <th rowspan="2">
                   <strong>STATUS</strong>
                 </th>
                 <th colspan="3">
                   <strong>CASHLESS</strong>
                 </th>
                 <th colspan="3">
                   <strong>REIMBURSEMENT</strong>
                 </th>
                 <th colspan="3">
                   <strong>TOTAL</strong>
                 </th>
               </tr>
               <tr>
                 <td>Total Claim</td>
                 <td>Claimed Amt</td>
                 <td>Incurred Amt</td>
                 <td>Total Claim</td>
                 <td>Claimed Amt</td>
                 <td>Incurred Amt</td>
                 <td>Total Claim</td>
                 <td>Claimed Amt</td>
                 <td>Incurred Amt</td>
               </tr>
               {Object.entries(enrollmentSummaryData.claim_status_summary || {}).map(
          ([status, data], index) => (
            <tr key={index} className="total-row">
              <td>{status}</td>
              <td>{data.Cashless?.count || 0}</td>
              <td>{data.Cashless?.claimed || 0}</td>
              <td>{data.Cashless?.incurred || 0}</td>
              <td>{data.Reimbursement?.count || 0}</td>
              <td>{data.Reimbursement?.claimed || 0}</td>
              <td>{data.Reimbursement?.incurred || 0}</td>
              <td>
                {(data.Cashless?.count || 0) + (data.Reimbursement?.count || 0)}
              </td>
              <td>
                {(data.Cashless?.claimed || 0) +
                  (data.Reimbursement?.claimed || 0)}
              </td>
              <td>
                {(data.Cashless?.incurred || 0) +
                  (data.Reimbursement?.incurred || 0)}
              </td>
            </tr>
          )
        )}
             </thead>
           </table>
         </div>
       </div>

      <div class="col-lg-12 col-md-12">
         <div class="row card-header">
           <table class="table table-bordered">
             <thead>
               <tr class="text-center">
                 <th colspan="5">AGE-WISE SETTLED CLAIM SUMMARY</th>
               </tr>
               <tr>
                 <th>
                   <strong>Age-Wise</strong>
                 </th>
                 <th>
                   <strong>Total Claims</strong>
                 </th>
                 <th>
                   <strong>Settled Amt</strong>
                 </th>
                 <th>
                   <strong>Percentage of Claims</strong>
                 </th>
                 <th>
                   <strong>ACS</strong>
                 </th>
               </tr>
               {enrollmentSummaryData.agewise_claim_summary?.map((item, index) => (
                  <tr key={index} className="total-row">
                    <td>{item["Age-Wise"]}</td>
                    <td>{item["Total Claims"]}</td>
                    <td>{item["Settled Amt"]}</td>
                    <td>{item["Percentage of Claims"]}</td>
                    <td>{item["ACS"]}</td>
                  </tr>
                ))}
             </thead>
           </table>
         </div>
       </div>

       <div class="col-lg-12 col-md-12">
         <div class="row card-header">
           <table class="table table-bordered">
             <thead>
               <tr class="text-center">
                 <th colspan="3">SETTLED CLAIM TAT SUMMARY</th>
               </tr>
               <tr>
                 <th>
                   <strong>Settled Claim Status</strong>
                 </th>
                 <th>
                   <strong>Internal Processing Tat (Number)</strong>
                 </th>
                 <th>
                   <strong>Internal Processing Tat (%)</strong>
                 </th>
               </tr>
               {enrollmentSummaryData.settled_claim_tat_summary?.map((item, index) => (
                  <tr key={index} className="total-row">
                    <td>{item["Settled Claim Status"]}</td>
                    <td>{item["Internal Processing Tat (Number)"]}</td>
                    <td>{item["Internal Processing Tat (%)"]}</td>
                  </tr>
                ))}
             </thead>
           </table>
         </div>
       </div>

       <div class="col-lg-12 col-md-12">
         <div class="row card-header">
           <table class="table table-bordered">
             <thead>
               <tr>
                 <th colspan="8">SETTLED (ACS)</th>
               </tr>
               <tr>
                 <th rowspan="2">
                   <strong>STATUS</strong>
                 </th>
                 <th colspan="3">
                   <strong>CASHLESS</strong>
                 </th>
                 <th colspan="3">
                   <strong>REIMBURSEMENT</strong>
                 </th>
               </tr>
               <tr>
                 <td>Total Claim</td>
                 <td>Settled Amt</td>
                 <td>ACS</td>
                 <td>Total Claim</td>
                 <td>Settled Amt</td>
                 <td>ACS</td>
               </tr>
               <tr class="total-row">
                 <td>Paid</td>
                 <td>{summaryPolicyData.settled_ACS_summary?.Cashless?.total_claims}</td>
                 <td>{summaryPolicyData.settled_ACS_summary?.Cashless?.settled_amt}</td>
                 <td>{summaryPolicyData.settled_ACS_summary?.Cashless?.acs}</td>
                 <td>{summaryPolicyData.settled_ACS_summary?.Reimbursement?.total_claims}</td>
                 <td>{summaryPolicyData.settled_ACS_summary?.Reimbursement?.settled_amt}</td>
                 <td>{summaryPolicyData.settled_ACS_summary?.Reimbursement?.acs}</td>
               </tr>
                <tr class="total-row">
                 <td>Total</td>
                 <td>{summaryPolicyData.settled_ACS_summary?.Cashless?.total_claims}</td>
                 <td>{summaryPolicyData.settled_ACS_summary?.Cashless?.settled_amt}</td>
                 <td>{summaryPolicyData.settled_ACS_summary?.Cashless?.acs}</td>
                 <td>{summaryPolicyData.settled_ACS_summary?.Reimbursement?.total_claims}</td>
                 <td>{summaryPolicyData.settled_ACS_summary?.Reimbursement?.settled_amt}</td>
                 <td>{summaryPolicyData.settled_ACS_summary?.Reimbursement?.acs}</td>
               </tr>
             </thead>
           </table>
         </div>
       </div>

       <div class="col-lg-12 col-md-12">
         <div class="row card-header">
           <table class="table table-bordered">
             <thead>
               <tr>
                 <th colspan="5">RELATION WISE SETTLED CLAIM SUMMARY</th>
               </tr>
               <tr>
                 <th>
                   <strong>Relation Wise</strong>
                 </th>
                 <th>
                   <strong>Total Claims</strong>
                 </th>
                 <th>
                   <strong>Settled Amt</strong>
                 </th>
                 <th>
                   <strong>Percentage of Claims</strong>
                 </th>
                 <th>
                   <strong>ACS</strong>
                 </th>
               </tr>
              
             </thead>
             <tbody>
        {summaryRelationBandWiseData.relationwise_claim_summary?.map((item, index) => (
          <tr key={index} className="total-row">
            <td>{item["Relation Wise"]}</td>
            <td>{item["Total Claims"]}</td>
            <td>{item["Settled Amt"]}</td>
            <td>{item["Percentage of Claims"]}</td>
            <td>{item["ACS"]}</td>
          </tr>
        ))}
      </tbody>
           </table>
         </div>
       </div>

       <div class="col-lg-12 col-md-12">
         <div class="row card-header">
           <table class="table table-bordered">
             <thead>
               <tr>
                 <th colspan="5">AMOUNT BAND SETTLED CLAIM SUMMARY</th>
               </tr>
               <tr>
                 <th>
                   <strong>AMOUNT BAND</strong>
                 </th>
                 <th>
                   <strong>Total Claims</strong>
                 </th>
                 <th>
                   <strong>Settled Amt</strong>
                 </th>
                <th>
                   <strong>% of Claims</strong>
                 </th>                 <th>
                   <strong>ACS</strong>
                 </th>
               </tr>
              
             </thead>
             <tbody>
             {summaryRelationBandWiseData.amount_band_summary?.map((item, index) => (
          <tr key={index} className="total-row">
            <td>{item["AMOUNT BAND"]}</td>
            <td>{item["Total Claims"]}</td>
            <td>{item["Settled Amt"]}</td>
            <td>{item["% of Claims"]}</td>
            <td>{item["ACS"]}</td>
          </tr>
        ))}
        </tbody>
           </table>
         </div>
       </div>

       <div class="col-lg-12 col-md-12">
         <div class="row card-header">
           <table class="table table-bordered">
             <thead>
               <tr>
                 <th colspan="8">LEVEL OF CARE SETTLED CLAIM SUMMARY</th>
               </tr>
               <tr>
                 <th rowspan="2">
                   <strong>Level of Care</strong>
                 </th>
                 <th colspan="3">
                   <strong>MEDICAL</strong>
                 </th>
                 <th colspan="3">
                   <strong>SURGICAL</strong>
                 </th>
               </tr>
               <tr>
                 <td>
                   <strong>Total Claim</strong>
                 </td>
                 <td>
                   <strong>Settled Amt</strong>
                 </td>
                 <td>
                   <strong>ACS</strong>
                 </td>
                 <td>
                   <strong>Total Claim</strong>
                 </td>
                 <td>
                   <strong>Settled Amt</strong>
                 </td>
                 <td>
                   <strong>ACS</strong>
                 </td>
               </tr>
              
             </thead>
             <tbody>
             {summaryRelationBandWiseData.level_of_care_summary?.map((item, index) => (
          <tr key={index} className="total-row">
            <td>{item["Level of Care"]}</td>
            <td>{item["Total Claim (Medical)"]}</td>
            <td>{item["Settled Amt (Medical)"]}</td>
            <td>{item["ACS (Medical)"]}</td>
            <td>{item["Total Claim (Surgical)"]}</td>
            <td>{item["Settled Amt (Surgical)"]}</td>
            <td>{item["ACS (Surgical)"]}</td>
            
          </tr>
        ))}
        </tbody>
          </table>
        </div>
     </div>

       <div class="col-lg-12 col-md-12">
         <div class="row card-header">
           <table class="table table-bordered">
             <thead>
               <tr>
                 <th colspan="9">
                   DISEASE CATEGORY WISE SETTLED CLAIM SUMMARY (BASE ON AMOUNT)
                 </th>
               </tr>
               <tr>
                 <td>
                   <strong>DISEASE CATEGORY</strong>
                 </td>
                <td colspan="2">
                  <strong>Cashless</strong>
                 </td>
                <td colspan="2">
                  <strong>Reimursement</strong>
                 </td>
                 <td colspan="2">
                   <strong>Total</strong>
                 </td>
                 
               </tr>
               
               {summaryData.top_diseases?.map((disease, index) => (
                <tr key={index} className="total-row">
                  <td>{disease.disease_category}</td>
                  <td>{disease.cashless_no}</td>
                  <td>{disease.cashless_amount}</td>
                  <td>{disease.reimbursement_no}</td>
                  <td>{disease.reimbursement_amount}</td>
                  <td>{disease.total_no}</td>
                  <td>{disease.total_amount}</td>
                </tr>
              ))}
             </thead>
           </table>
         </div>
       </div>

       <div class="col-lg-12 col-md-12">
         <div class="row card-header">
           <table class="table table-bordered">
             <thead>
               <tr>
                 <th colspan="8">TOP HOSPITAL-WISE SETTLED CLAIM SUMMARY (BASED ON AMOUNT)</th>
               </tr>
               <tr>
                 <th rowspan="2">
                   <strong>HOSPITAL NAME WITH CITY</strong>
                 </th>
                 <th rowSpan="2">
                   <strong>NETWORK TYPE</strong>
                 </th>
                 <th colspan="2">
                   <strong>CASHLESS</strong>
                 </th>
                 <th colspan="2">
                   <strong>REIMBURSEMENT</strong>
                 </th>
                 <th colspan="2">
                   <strong>TOTAL</strong>
                 </th>
               </tr>
               <tr>
                 <td>
                   <strong>NO</strong>
                 </td>
                 <td>
                   <strong>AMOUNT</strong>
                 </td>
                 <td>
                   <strong>NO</strong>
                 </td>
                 <td>
                   <strong>AMOUNT</strong>
                 </td>
                 <td>
                   <strong>NO</strong>
                 </td>
                 <td>
                   <strong>AMOUNT</strong>
                 </td>
               </tr>
               {summaryData.hospital_claims?.map((claim, index) => (
                <tr key={index} className="total-row">
                  <td>{claim.hospital_name}</td>
                  <td>{claim.network_type}</td>
                  <td>{claim.cashless_no}</td>
                  <td>{claim.cashless_amount}</td>
                  <td>{claim.reimbursement_no}</td>
                  <td>{claim.reimbursement_amount}</td>
                  <td>{claim.total_no}</td>
                  <td>{claim.total_amount}</td>
                </tr>
              ))}
             </thead>
          </table>
        </div>
     </div>
      
    </Card>
  );
  const getTabIcon = (type) => {
      switch (type) {
        case "GMC":
          return <TeamOutlined style={{ marginRight: 8 }} />;
        case "GTL":
          return <UserOutlined style={{ marginRight: 8 }} />;
        case "GPA":
          return <TeamOutlined style={{ marginRight: 8 }} />;
          case "Parent policy":
          return <TeamOutlined style={{ marginRight: 8 }} />;
          case "Topup":
          return <FileAddOutlined style={{ marginRight: 8 }} />;
         case "Fire":
          return <FireOutlined style={{ marginRight: 8 }} />;
        default:
          return null;
      }
    };
  
  const tabItems = policyTypes?.map((type) => ({
    key: type,
    label: (
      <span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
    
        {type}
        {getTabIcon(type)}
      </span>
    ),
    children: (
      <>
        <Tabs
          activeKey={
            selectedPolicyNumber 
              ? (selectedPolicyNumber.policy_number || 
                 `policy-${escalationData[type]?.findIndex(p => p.policy_id === selectedPolicyNumber.policy_id)}`)
              : undefined
          }
          onChange={handlePolicyNumberClick}
          items={escalationData[type]?.map((policy, index) => ({
            key: policy.policy_number || `policy-${index}`,
            label: policy.policy_number ? (
              policy.policy_number
            ) : (
              <span
                style={{
                  backgroundColor: "#FFFF00",
                  color: "#000000E0",
                  padding: "2px 8px",
                  fontSize: "12px",
                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                  borderRadius: "4px",
                  display: "inline-block",
                }}
              >
                Policy Number not issued
              </span>
            ),
          }))}
        />
        {selectedPolicyNumber ? (
          <EscalationLevels
            levels={
              escalationData[type]?.filter(
                (policy) => policy.policy_number === selectedPolicyNumber.policy_number
              ) || []
            }
          />
        ) : (
          !loading && <div className="escalation-message">No Data Found for the selected policy.</div>
        )}
      </>
    ),
  }));

  return (
    <Spin spinning={loading}>
      <div className="escalation-matrix-container">
        <div className="escalation-heading">Summary</div>
        <div className="escalation-content">
           <Row className="escalation-row-header">
           <span style={{ fontWeight: "700", fontSize: "20px" }}>
            {user?.corporate_name || "Guest"}
            </span>{" "}
            {/* --- Updated to use Redux state --- */}
            <span style={{ marginLeft: "8px" }}>
              (Financial Year: {selectedYearName})
            </span>
           </Row>
          </div>
          {policyTypes.length > 0 ? (
            <Tabs
              activeKey={activeTab}
              onChange={handleTabChange}
              className="esc-tab"
              items={tabItems}
            />
          ) : (
            <Card style={{ marginTop: "20px" }}>
              <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
                <p style={{ fontSize: "16px", fontWeight: "500" }}>No summary data available for {selectedYearName}</p>
              </div>
            </Card>
          )}
          
        </div>
      
    </Spin>
  );
};

export default Summary;